import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { formatDate } from '../../utils/dateFormatter';
import { canWrite } from '../../utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const GeneralLedger = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [newType, setNewType] = useState('');
  const [newTypeAr, setNewTypeAr] = useState('');
  const [addingType, setAddingType] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    transaction_type: '',
    description: '',
    debit: 0,
    credit: 0
  });

  const getTypeName = (typeName) => {
    if (language !== 'ar') return typeName;
    const type = transactionTypes.find(t => t.name === typeName);
    return type?.name_ar || typeName;
  };

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/ledger`, { headers: getAuthHeader() });
      setEntries(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Failed to fetch ledger entries');
    }
  };

  const AUTOMATED_TYPES = ['Client Payment', 'Supplier Payment', 'Withdrawal', 'Outsourcing', 'Salaries'];

  const fetchTransactionTypes = async () => {
    try {
      const response = await axios.get(`${API}/ledger-transaction-types`, { headers: getAuthHeader() });
      setTransactionTypes(response.data);
      const manual = response.data.filter(t => !AUTOMATED_TYPES.includes(t.name));
      if (manual.length > 0) {
        setFormData(prev => ({ ...prev, transaction_type: manual[0].name }));
      }
    } catch (error) {
      toast.error('Failed to fetch transaction types');
    }
  };

  useEffect(() => {
    fetchEntries();
    fetchTransactionTypes();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/ledger`, formData, { headers: getAuthHeader() });
      toast.success('Entry added successfully');
      setIsOpen(false);
      fetchEntries();
      setFormData({
        date: new Date().toISOString().split('T')[0],
        transaction_type: transactionTypes.filter(t => !AUTOMATED_TYPES.includes(t.name))[0]?.name || '',
        description: '',
        debit: 0,
        credit: 0
      });
    } catch (error) {
      toast.error('Failed to add entry');
    }
  };

  const handleAddType = async () => {
    if (!newType.trim() || !newTypeAr.trim()) return;
    try {
      await axios.post(`${API}/ledger-transaction-types`, { name: newType.trim(), name_ar: newTypeAr.trim() }, { headers: getAuthHeader() });
      toast.success('Type added');
      setNewType('');
      setNewTypeAr('');
      setAddingType(false);
      fetchTransactionTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add type');
    }
  };

  const handleDelete = async (date, description) => {
    if (user.role !== 'admin') {
      toast.error('Only admin can delete');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this ledger entry?')) return;
    try {
      await axios.delete(`${API}/ledger/${date}/${description}`, { headers: getAuthHeader() });
      toast.success('Entry deleted');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  return (
    <Card className="p-6 border border-border">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('cashBook')}</h2>
        {canWrite(user?.role) && <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-accent hover:bg-accent/90" data-testid="add-ledger-btn">
              <Plus className="w-4 h-4" />
              <span lang={language === 'ar' ? 'ar' : 'en'}>{t('add')}</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>{t('addCashBookEntry')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('date')}</Label>
                <p className="text-sm px-3 py-2 rounded-md border border-input bg-muted text-muted-foreground">{formData.date}</p>
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('transactionType')}</Label>
                <div className="flex gap-2">
                  <select
                    value={formData.transaction_type}
                    onChange={(e) => setFormData({...formData, transaction_type: e.target.value})}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    required
                    data-testid="ledger-type-select"
                  >
                    {transactionTypes.filter(type => !AUTOMATED_TYPES.includes(type.name)).map((type) => (
                      <option key={type.name} value={type.name}>{language === 'ar' ? type.name_ar : type.name}</option>
                    ))}
                  </select>
                  {user?.role === 'admin' && (
                    <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={() => setAddingType(!addingType)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {addingType && (
                  <div className="flex flex-col gap-2 mt-2">
                    <div className="flex gap-2">
                      <Input
                        placeholder="English name"
                        value={newType}
                        onChange={(e) => setNewType(e.target.value)}
                        autoFocus
                      />
                      <Input
                        placeholder="الاسم بالعربي"
                        value={newTypeAr}
                        onChange={(e) => setNewTypeAr(e.target.value)}
                        dir="rtl"
                      />
                    </div>
                    <Button type="button" size="sm" onClick={handleAddType} className="bg-accent hover:bg-accent/90 self-end" disabled={!newType.trim() || !newTypeAr.trim()}>
                      {t('save')}
                    </Button>
                  </div>
                )}
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('description')}</Label>
                <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required data-testid="ledger-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('debit')}</Label>
                  <Input type="number" step="0.01" value={formData.debit} onChange={(e) => setFormData({...formData, debit: parseFloat(e.target.value)})} data-testid="ledger-debit-input" />
                </div>
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('credit')}</Label>
                  <Input type="number" step="0.01" value={formData.credit} onChange={(e) => setFormData({...formData, credit: parseFloat(e.target.value)})} data-testid="ledger-credit-input" />
                </div>
              </div>
              <div className="bg-muted/30 p-3 rounded-md text-sm">
                <p className="text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>{t('balanceAutoCalc')}</p>
                <p className="text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>{t('debitAddsCredit')}</p>
              </div>
              <Button type="submit" className="w-full bg-accent hover:bg-accent/90" data-testid="ledger-submit-btn">
                <span lang={language === 'ar' ? 'ar' : 'en'}>{t('save')}</span>
              </Button>
            </form>
          </DialogContent>
        </Dialog>}
      </div>

      <div className="flex gap-4 mb-4 items-end">
        <div>
          <Label className="text-xs text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>{t('from')}</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>{t('to')}</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40" />
        </div>
        {(fromDate || toDate) && (
          <Button variant="ghost" size="sm" onClick={() => { setFromDate(''); setToDate(''); }} className="text-muted-foreground">
            <span lang={language === 'ar' ? 'ar' : 'en'}>{t('clear')}</span>
          </Button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-muted/50">
            <tr>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('date')}</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('type')}</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('description')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('debit')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('credit')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('balance')}</th>
              {user?.role === 'admin' && <th className="border border-border p-3 text-center font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {entries.filter(e => (!fromDate || e.date >= fromDate) && (!toDate || e.date <= toDate)).map((entry, idx) => (
              <tr key={idx} className="hover:bg-muted/30" data-testid={`ledger-row-${idx}`}>
                <td className="border border-border p-3">{formatDate(entry.date)}</td>
                <td className="border border-border p-3">{getTypeName(entry.transaction_type)}</td>
                <td className="border border-border p-3">{entry.description}</td>
                <td className="border border-border p-3 text-end font-mono-numeric text-success">{entry.debit.toFixed(2)}</td>
                <td className="border border-border p-3 text-end font-mono-numeric text-destructive">{entry.credit.toFixed(2)}</td>
                <td className="border border-border p-3 text-end font-mono-numeric font-semibold">{entry.balance.toFixed(2)}</td>
                {user?.role === 'admin' && (
                  <td className="border border-border p-3 text-center">
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.date, entry.description)} data-testid={`delete-ledger-${idx}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {entries.length === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="empty-ledger">
            <p lang={language === 'ar' ? 'ar' : 'en'}>{t('noEntriesYet')}</p>
          </div>
        )}
      </div>
    </Card>
  );
};
